import { useContext, useEffect, useRef, useState } from "react";
import FileReactPlayer from "react-player/file";
import YoutubeReactPlayer from "react-player/youtube";

import { ResourceTitle } from "./controls/resource_title";

import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { setResourceVideoState, setResourceVideoTimeInSeconds } from "shared/reducers/classroom";
import { CapabilityType, ResourceType, ResourceTypeType, VideoStateType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";
import { isMuxVideo, needsSignatureToAccess } from "utils/resource";

import "./video_resource.scss";

const TOOLBAR_HEIGHT = 32;

// Supports YouTube videos, Mux video streams, and mp4 files
function VideoResource({ resource }: { resource: ResourceType }) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const isInBreakoutControlRoom = useAppSelector(
    classroomSelectors.selectCurrentUserIsInBreakoutControlRoom(),
  );
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const [currentUserInitiatedPlay, setCurrentUserInitiatedPlay] = useState(false);
  const [resourceUrl, setResourceUrl] = useState(resource.url);
  const filePlayerRef = useRef<FileReactPlayer>(null);
  const youtubePlayerRef = useRef<YoutubeReactPlayer>(null);
  const _isMuxVideo = isMuxVideo(resource.type, resource.url, resource.mux_details);
  const shouldBeMuted = (isInPerson && !currentUserInitiatedPlay) || isInBreakoutControlRoom;
  const canControlVideo = (canControlClassroom || !!activeBreakoutSessionId) && !isPresentationView;

  useEffect(() => {
    async function getResourceUrl() {
      if (_isMuxVideo) {
        const token = await forumAPI.getMuxVideoToken(resource.id);
        const playbackId = resource.mux_details?.playback_id;
        setResourceUrl(`https://stream.mux.com/${playbackId}.m3u8?token=${token}`);
      } else if (needsSignatureToAccess(resource.type, resource.url)) {
        const url = await forumAPI.getSignedUrl(resource.id, { contentDisposition: "inline" });
        setResourceUrl(url);
      }
    }
    getResourceUrl();
  }, [_isMuxVideo, resource.id, resource.mux_details, resource.type, resource.url, forumAPI]);

  useEffect(() => {
    // Note: currentUserInitiatedPlay prevents us from getting into an infinite loop of playing and seeking
    if (resource.video_time_in_seconds && !currentUserInitiatedPlay) {
      getPlayerRef()?.seekTo(resource.video_time_in_seconds);
    }
  }, [currentUserInitiatedPlay, resource.video_time_in_seconds]);

  // When the component is destroyed, update the resource
  useEffect(() => {
    return () => {
      if (canControlVideo) {
        dispatch(
          setResourceVideoState({ resourceId: resource.id, videoState: VideoStateType.Paused }),
        );
      }
    };
  }, [dispatch, resource.id, canControlVideo]);

  const getPlayerRef = () => {
    return youtubePlayerRef.current || filePlayerRef.current;
  };

  const onPlay = () => {
    const videoTimeInSeconds = getPlayerRef()?.getCurrentTime();
    window.flightLogger.log(
      `[VideoResource] onPlay userId=${currentUserId} videoTimeInSeconds=${videoTimeInSeconds}`,
    );
    if (canControlVideo) {
      setCurrentUserInitiatedPlay(true);
      dispatch(
        setResourceVideoState({ resourceId: resource.id, videoState: VideoStateType.Playing }),
      );
      dispatch(setResourceVideoTimeInSeconds({ resourceId: resource.id, videoTimeInSeconds }));
    }
  };

  const onPause = () => {
    const videoTimeInSeconds = getPlayerRef()?.getCurrentTime();
    window.flightLogger.log(
      `[VideoResource] onPause userId=${currentUserId} videoTimeInSeconds=${videoTimeInSeconds}`,
    );
    if (canControlVideo) {
      dispatch(
        setResourceVideoState({ resourceId: resource.id, videoState: VideoStateType.Paused }),
      );
    }
  };

  // Avoid loading the player until Mux url is ready
  // https://github.com/cookpete/react-player/issues/1462
  if (_isMuxVideo && !resourceUrl.startsWith("https://stream.mux.com")) return null;

  return (
    <div
      className="flex flex-column w-full h-full"
      style={{ pointerEvents: canControlVideo ? "auto" : "none" }}
    >
      <div className="flex items-center p1 bg-black body-s text-black-tint-70 resource-controls">
        <ResourceTitle resource={resource} />
      </div>

      <div className="relative" style={{ height: `calc(100% - ${TOOLBAR_HEIGHT}px)` }}>
        {resource.type === ResourceTypeType.Video ? (
          <YoutubeReactPlayer
            className="react-player"
            ref={youtubePlayerRef}
            url={resourceUrl}
            controls={canControlVideo}
            playing={resource.video_state === VideoStateType.Playing}
            onPlay={onPlay}
            onPause={onPause}
            muted={!!shouldBeMuted}
            volume={1}
          />
        ) : (
          <FileReactPlayer
            className="react-player"
            ref={filePlayerRef}
            url={resourceUrl}
            controls={canControlVideo}
            playing={resource.video_state === VideoStateType.Playing}
            onPlay={onPlay}
            onPause={onPause}
            muted={!!shouldBeMuted}
            volume={1}
            config={{
              attributes: {
                controlslist: "nodownload noremoteplayback noplaybackrate",
                disablepictureinpicture: "1",
              },
            }}
          />
        )}
      </div>
    </div>
  );
}

export { VideoResource };
